import { InsightsMetricsSplit } from "@octopusdeploy/octopus-server-client";
import type { InsightsReportResource } from "@octopusdeploy/octopus-server-client";
import type { ReportMetricsPageProps } from "app/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import { ReportMetricsLayout } from "app/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import React, { useEffect } from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { getChartAxisDateFormatter } from "../../dataTransformation/dateHelpers";
import { getChartReadyInsightsData, getYAxisUnitOfTimeKeys } from "../../dataTransformation/getChartReadyInsightsData";
import { getEvenlySpacedTickArray, getYAxisDataMax } from "../../dataTransformation/getYAxisDataMax";
import { formatDurationInWords } from "../../dataTransformation/stringHelpers";
import { insightsCadenceLookup, insightsCadenceXAxisTickInterval } from "../../insightsCadence";
import { InsightsTooltip } from "../ChartTooltips/InsightsTooltip/InsightsTooltip";
import LineChart from "../Charts/LineChart";
import { DownloadReportCsvButton } from "../DownloadReportCsvButton";
import { InsightsChartCard } from "../InsightsChartCard";
import { TimeToRecoveryTrendTables } from "./TrendTables/TimeToRecoveryTables";

type Props = ReportMetricsPageProps;

export function ReportMeanTimeToRecoveryInner({ report, bffResponse, split, cadence, busy }: Props) {
    const dispatchAction = useAnalyticSimpleActionDispatch();

    const chartData = getChartReadyInsightsData(bffResponse.Series);

    const { MeanTimeToRecoveryKey } = getYAxisUnitOfTimeKeys(chartData);

    const maxYAxisValue = getYAxisDataMax(chartData, `MeanTimeToRecovery.mean.${MeanTimeToRecoveryKey}`);

    const currentGranularity = insightsCadenceLookup[cadence].granularity;

    const dateFormatter = getChartAxisDateFormatter(currentGranularity);

    const showSeries = split !== InsightsMetricsSplit.None;

    useEffect(() => {
        dispatchAction("View Insights Report Mean Time to Recovery");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <main>
            <section style={{ padding: "0rem 1rem 2rem" }}>
                <InsightsChartCard height={500}>
                    <LineChart
                        data={chartData}
                        loading={Boolean(busy)}
                        dataKey={`MeanTimeToRecovery.mean.${MeanTimeToRecoveryKey}`}
                        yAxisLabel={`Avg. no. of ${MeanTimeToRecoveryKey.toLowerCase()}`}
                        xAxisDataKey="StartOfInterval"
                        xAxisType="category"
                        yAxisType="number"
                        yAxisDomain={[0, maxYAxisValue]}
                        yAxisTickArray={getEvenlySpacedTickArray(maxYAxisValue)}
                        xTickFormatter={dateFormatter}
                        tooltip={<InsightsTooltip showSeries={showSeries} dataKey="MeanTimeToRecovery.mean.duration" valueFormatter={formatDurationInWords} cadence={cadence} />}
                        xAxisTickInterval={insightsCadenceXAxisTickInterval[cadence]}
                        showLegend={showSeries}
                    />
                </InsightsChartCard>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <DownloadReportCsvButton report={report} />
                </div>
            </section>
            <section>
                <TimeToRecoveryTrendTables trends={bffResponse.MeanTimeToRecovery} />
            </section>
        </main>
    );
}

export function ReportMeanTimeToRecovery({ report }: { report: InsightsReportResource }) {
    return (
        <ReportMetricsLayout title="Mean time to recovery" report={report} trendKey="MeanTimeToRecovery">
            {(props) => <ReportMeanTimeToRecoveryInner {...props} />}
        </ReportMetricsLayout>
    );
}
